import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Building2, TrendingUp, Home, Network } from "lucide-react";
import { SOLUTIONS_IMAGES } from "@/lib/site";

/**
 * RelatedSolutions — "You might also be interested in" strip.
 * Shown at the bottom of every Solutions subpage (above the footer).
 * Renders the other 4 solution areas as compact image-cards, filtering out the current slug.
 */
const ALL = [
  { slug: "brokerages",     icon: Briefcase,  title: "Brokerages",     desc: "Capture, qualify and convert more leads with CRM intelligence and automation.",      img: SOLUTIONS_IMAGES.cardBrokerages },
  { slug: "developers",     icon: Building2,  title: "Developers",     desc: "Unify sales, inventory and marketing across every project.",                        img: SOLUTIONS_IMAGES.cardDevelopers },
  { slug: "investors",      icon: TrendingUp, title: "Investors",      desc: "Portfolio visibility and data-driven investment decisions.",                        img: SOLUTIONS_IMAGES.cardInvestors },
  { slug: "property-teams", icon: Home,       title: "Property Teams", desc: "Streamline operations, engage tenants and improve efficiency across properties.",  img: SOLUTIONS_IMAGES.cardPropertyTeams },
  { slug: "enterprises",    icon: Network,    title: "Enterprises",    desc: "Operate complex portfolios with enterprise-grade intelligence and governance.",    img: SOLUTIONS_IMAGES.cardEnterprises },
];

export default function RelatedSolutions({ currentSlug }) {
  const others = ALL.filter((s) => s.slug !== currentSlug);
  return (
    <section className="relative pb-20 lg:pb-24" data-testid="related-solutions">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="flex items-end justify-between gap-6 mb-8">
          <div className="fade-up">
            <div className="text-[10.5px] tracking-[0.38em] text-sapphire-glow uppercase font-medium mb-3">
              EXPLORE MORE
            </div>
            <h2 className="font-display text-platinum text-[26px] sm:text-[32px] lg:text-[36px] leading-tight tracking-[-0.018em]">
              You might also be interested in
            </h2>
          </div>
          <Link to="/solutions" data-testid="related-view-all"
            className="hidden sm:inline-flex items-center gap-1.5 text-[12px] text-platinum/70 hover:text-sapphire-glow transition-colors whitespace-nowrap">
            View all solutions <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {others.map((s, i) => {
            const Icon = s.icon;
            return (
              <Link
                to={`/solutions/${s.slug}`}
                key={s.slug}
                data-testid={`related-card-${s.slug}`}
                className="group relative rounded-2xl overflow-hidden border border-white/[0.07] hover:border-sapphire-glow/40 transition-all duration-500 fade-up"
                style={{
                  background: "linear-gradient(180deg, rgba(15,22,40,0.55) 0%, rgba(10,15,26,0.92) 100%)",
                  animationDelay: `${i * 70}ms`,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 0 36px -8px rgba(96,165,250,0.40)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
              >
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/45 to-transparent" />

                {/* Image with cinematic blend (homepage-style) */}
                <div className="relative h-[150px] sm:h-[170px] overflow-hidden">
                  <div className="absolute inset-0 pointer-events-none"
                    style={{ background: "radial-gradient(60% 60% at 60% 50%, rgba(37,99,235,0.32), transparent 75%)", filter: "blur(28px)" }} aria-hidden />
                  <img src={s.img} alt={`${s.title} — Esthera RealityOS solution`}
                    className="block w-full h-full object-cover object-center transition-transform duration-[1200ms] group-hover:scale-[1.06]"
                    style={{
                      WebkitMaskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.55) 80%, transparent 100%)",
                      maskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.55) 80%, transparent 100%)",
                    }}
                    draggable="false" />
                  {/* bottom fade into card body */}
                  <div className="absolute inset-0 pointer-events-none"
                    style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.30) 0%, rgba(10,15,26,0) 30%, rgba(10,15,26,0) 60%, rgba(10,15,26,0.95) 100%)" }} aria-hidden />
                </div>

                <div className="p-5 lg:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full inline-flex items-center justify-center"
                      style={{
                        background: "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.12), #0E1422 75%)",
                        border: "1px solid rgba(96,165,250,0.55)",
                        boxShadow: "0 0 14px -3px rgba(96,165,250,0.45), inset 0 0 8px rgba(96,165,250,0.10)",
                      }}>
                      <Icon className="w-4 h-4" style={{ color: "#60A5FA" }} />
                    </div>
                    <div className="font-display text-platinum text-[16px] sm:text-[17px] leading-[1.2] tracking-tight">{s.title}</div>
                  </div>
                  <p className="text-[12.5px] sm:text-[13px] text-platinum/60 leading-[1.6] mb-4 min-h-[44px]">{s.desc}</p>
                  <div className="inline-flex items-center gap-1.5 text-[12px] text-sapphire-glow font-medium">
                    Learn more
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
